import { apiRequest } from "./client";

export interface CheckoutSession {
  reference: string;
  /** The address Paystack keys the charge to. */
  email: string;
  /** The link a student sends a parent. */
  shareableUrl: string;
  amountMinor: number;
  currency: string;
}

/**
 * Start a checkout for the signed-in student.
 *
 * No `studentId`: the backend takes it from the token. Passing one was an IDOR
 * and the parameter no longer exists.
 */
export function startCheckout(payerEmail?: string): Promise<CheckoutSession> {
  return apiRequest<CheckoutSession>("/api/checkout", {
    method: "POST",
    body: payerEmail ? { payerEmail } : {},
    authenticated: true,
  });
}

export interface SharedCheckout {
  reference: string;
  email: string;
  amountMinor: number;
  currency: string;
  status: string;
  accessEndsAt: string;
  /** Screen 22b leads with what the child has actually done. */
  childName: string;
  daysPractisedOfLastSeven: number;
}

/** What the parent's payment page reads — no auth, opaque reference. */
export function fetchSharedCheckout(reference: string): Promise<SharedCheckout> {
  return apiRequest<SharedCheckout>(`/api/checkout/${reference}`);
}

/**
 * What to do next, decided by the server rather than read out of prose.
 *
 * `pending` means wait and ask again; `failed` means the charge is finished
 * and did not succeed, and is the only one that should offer to pay again.
 * `review` means the money arrived but does not match what we asked for, so a
 * person has to look — inviting a second payment there would take more money
 * for the same unresolved problem.
 */
export type CheckoutStatus = "active" | "pending" | "failed" | "review";

export interface CheckoutVerification {
  activated: boolean;
  status: CheckoutStatus;
  reason?: string;
}

/**
 * Confirm a charge the Paystack sheet reported as successful.
 *
 * The sheet hands back a reference and nothing else; this asks our backend to
 * ask Paystack whether it was really paid. The browser's word grants nothing,
 * which is the whole point of the round trip.
 *
 * "pending" is a normal answer, not a failure: bank transfer and USSD confirm
 * asynchronously and the webhook finishes the job.
 */
export function verifyCheckout(reference: string): Promise<CheckoutVerification> {
  return apiRequest<CheckoutVerification>(`/api/checkout/${reference}/verify`, {
    method: "POST",
    body: {},
  });
}

export type ShareChannel = "sms" | "whatsapp" | "email";

export interface SharedWithParent {
  reference: string;
  channel: ShareChannel;
  /** Normalised, so a student who mistyped can see where it went. */
  sentTo: string;
  link: string;
}

/**
 * Have the server text or email a parent the payment link.
 *
 * Only the channel and the contact go up. The message, the amount and the
 * reference are the server's: text supplied by a client and delivered to a
 * number a client chose would be an open relay carrying our sender name.
 */
export function shareCheckoutWithParent(input: {
  channel: ShareChannel;
  contact: string;
}): Promise<SharedWithParent> {
  return apiRequest<SharedWithParent>("/api/checkout/share", {
    method: "POST",
    body: input,
    authenticated: true,
  });
}
